import clsx from "clsx";
import { JSX } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger
} from "../dropdownMenu/DropdownMenu";
import { navigationActive } from "../dropdownMenu/DropdownMenu.css";
import { Icon } from "../icon/Icon";
import {
  navMenuItemLabelText,
  navMenuItemSelected,
  navMenuItemSelectedLabel
} from "./NavigationMenu.css";
import { NavigationMenuItem } from "./NavigationMenuItem";

interface NavigationItem {
  /**
   * The value of the navigation item.
   */
  value: string;

  /**
   * The label to display for the navigation item.
   */
  label: string;

  /**
   * Optional sub-items
   */
  children?: NavigationItem[];

  /**
   * Handler function called when the item is selected.
   */
  onSelect?: () => void;
}

export interface NavigationMenuProps {
  items: NavigationItem[];

  /**
   * Initial selected item
   */
  selectedItem?: {
    value: string;
    label: string;
  };

  /**
   * Text displayed as a group placeholder, shown alongside the selected item for grouped dropdowns.
   */
  groupPlaceholder?: string;

  /**
   * Text displayed when no item is selected and no group placeholder is provided.
   */
  placeholder?: string;
}

export function NavigationMenu({
  selectedItem,
  items,
  groupPlaceholder,
  placeholder
}: NavigationMenuProps): JSX.Element {
  const flattenedItems = items.flatMap((item) => [
    { ...item, isSubItem: false },
    ...(item.children?.map((subItem) => ({ ...subItem, isSubItem: true })) ??
      [])
  ]);

  return (
    <DropdownMenu modal>
      <DropdownMenuTrigger className={selectedItem && navigationActive}>
        <div className={navMenuItemSelectedLabel}>
          <div className={navMenuItemLabelText}>
            {groupPlaceholder ?? placeholder}
          </div>
          {selectedItem && (
            <>
              <Icon name="diagonal-line" />
              <span className={clsx(navMenuItemLabelText, navMenuItemSelected)}>
                {selectedItem.label}
              </span>
            </>
          )}
        </div>
      </DropdownMenuTrigger>

      <DropdownMenuContent side="bottom" align="start" role="listbox">
        {flattenedItems.map((item, index) => (
          <NavigationMenuItem
            data-testid="navigation-menu"
            key={index}
            isSelected={selectedItem?.value === item.value}
            onSelect={() => {
              item.onSelect?.();
            }}
            role={item.children ? "group" : "menuitem"}
          >
            {item.isSubItem && <Icon name="dot" />}
            {item.label}
          </NavigationMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
